import React, { useState, useEffect, useMemo } from "react";
import Modal from "../../components/common/modal/Modal";
import ButtonComponent from "../../components/common/button/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { toast } from "react-toastify";
import { SingleCheckboxDropdown } from "../../components/common/SingleCheckboxDropdown";

const AddPlantConfigurationModal = ({
    open,
    onClose,
    onSubmit,
    plantOptions,
    allData,
    editData,
    parentVendorOptions,
    childVendorOptions,
    categoryOptions,
}) => {
    const initialRow = {
        rowKey: Date.now(),
        plantCode: null,
        parentVendorId: null,
        childVendorId: null,
        businessCategory: null,
        reviewPeriod: "",
    };

    const [rows, setRows] = useState([initialRow]);

    const resetForm = () => {
        setRows([
            {
                rowKey: Date.now(),
                plantCode: null,
                parentVendorId: null,
                childVendorId: null,
                businessCategory: null,
                reviewPeriod: "",
            },
        ]);
    };

    const handleAddRow = () => {
        setRows((prev) => [
            ...prev,
            {
                rowKey: Date.now(),
                plantCode: [],
                parentVendorId: [],
                childVendorId: [],
                businessCategory: [],
                reviewPeriod: "",
            },
        ]);
    };

    const handleDelete = (index) => {
        setRows((prev) =>
            prev.filter((_, i) => i !== index)
        );
    };

    const updateField = (index, field, value) => {
        const updated = [...rows];
        updated[index][field] = value;
        setRows(updated);
    };

    const getSelectedPlantValues = (row) =>
        row.plantCode?.value ? [row.plantCode.value] : [];

    const getFilteredOptions = (row, currentField, optionList, valueKey) => {
        const filteredRows = allData.filter(r => {
            return (
                (currentField === "plantCode" ||
                    !row.plantCode?.value ||
                    r.plant === row.plantCode.value) &&

                (currentField === "parentVendorId" ||
                    !row.parentVendorId?.value ||
                    r.parentVendor === row.parentVendorId.value) &&

                (currentField === "childVendorId" ||
                    !row.childVendorId?.value ||
                    r.vendorCode === row.childVendorId.value) &&

                (currentField === "businessCategory" ||
                    !row.businessCategory?.value ||
                    r.businessCategory === row.businessCategory.value)
            );
        });

        const allowedValues = new Set(
            filteredRows
                .map(r => r[valueKey])
                .filter(Boolean)
        );

        return optionList.filter(opt =>
            allowedValues.has(opt.value)
        );
    };

    const allRowsValid = rows.every(
        (row) =>
            row.reviewPeriod &&
            (
                row.plantCode ||
                row.parentVendorId ||
                row.childVendorId
            )
    );

    useEffect(() => {
        if (editData) {
            setRows([
                {
                    rowKey: Date.now(),
                    plantCode: editData.plantCode?.[0] || null,
                    parentVendorId: editData.parentVendorId?.[0] || null,
                    childVendorId: editData.childVendorId?.[0] || null,
                    businessCategory: editData.businessCategory?.[0] || null,
                    reviewPeriod: editData.reviewPeriod ?? ""
                }
            ]);
        }
    }, [editData, parentVendorOptions, childVendorOptions, plantOptions, categoryOptions]);

    const handleSubmit = async () => {
        const invalidRow = rows.find(
            row => Number(row.reviewPeriod) < 0
        );

        if (invalidRow) {
            toast.error("Review Period cannot be negative");
            return;
        }
        const payload = rows.map((r) => ({
            plantIds: r.plantCode ? [r.plantCode.value] : [],
            plantNames: r.plantCode
                ? r.plantCode.label?.split(" | ")[1] || r.plantCode.label
                : "",

            parentVendorIds: r.parentVendorId
                ? [`${r.parentVendorId.value}|${r.parentVendorId.label?.split(" | ")[1] || ""}`]
                : [],

            parentVendorNames: r.parentVendorId
                ? r.parentVendorId.label?.split(" | ")[1] || r.parentVendorId.label
                : "",

            childVendorIds: r.childVendorId
                ? [`${r.childVendorId.value}|${r.childVendorId.label?.split(" | ")[1] || ""}`]
                : [],

            childVendorNames: r.childVendorId
                ? r.childVendorId.label?.split(" | ")[1] || r.childVendorId.label
                : "",

            businessCategories: r.businessCategory
                ? [r.businessCategory.value]
                : [],

            reviewPeriodDays: Number(r.reviewPeriod)
        }));

        const result = await onSubmit(payload);

        if (result !== false) {
            resetForm();
            onClose();
        }
    };

    const isRowValid = (row) => {
        const hasEntity =
            !!row.plantCode ||
            !!row.parentVendorId ||
            !!row.childVendorId;

        const hasReviewPeriod =
            row.reviewPeriod !== "" &&
            row.reviewPeriod !== null &&
            row.reviewPeriod !== undefined;

        return hasEntity && hasReviewPeriod;
    };
    const isAllRowsValid = rows.every(isRowValid);

    const handleUpdateSubmit = async () => {
        const r = rows[0]; // edit always single row in modal
        const reviewPeriod = Number(r.reviewPeriod || 0);

        if (reviewPeriod < 0) {
            toast.error("Review Period cannot be negative");
            return;
        }

        const payload = {
            id: editData?.id,

            plantId: r.plantCode?.value || "",
            plantName: r.plantCode?.label?.split(" | ")[1] || "",

            parentVendorId: r.parentVendorId?.value || "",
            parentVendorName: r.parentVendorId?.label?.split(" | ")[1] || "",

            childVendorId: r.childVendorId?.value || "",
            childVendorName: r.childVendorId?.label?.split(" | ")[1] || "",

            businessCategory: r.businessCategory?.value || "",

            reviewPeriodDays: Number(r.reviewPeriod || 0)
        };

        const result = await onSubmit(payload);

        if (result !== false) {
            resetForm();
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            handleClose={() => { onClose(); resetForm() }}
            className="AddPlantModal_cls"
            title="Add Plant Configuration"
            fullWidth={true}
            maxWidth={"lg"}
            action={
                <div style={{ display: "flex", gap: 10 }}>
                    <ButtonComponent
                        label="Back"
                        type="primary"
                        onClick={() => {
                            resetForm();
                            onClose();
                        }}
                    />

                    <ButtonComponent
                        label="Clear All"
                        type="primary"
                        onClick={resetForm}
                    />

                    <ButtonComponent
                        label={editData ? "Update" : "Submit"}
                        type="secondary"
                        disabled={!allRowsValid}
                        onClick={editData ? handleUpdateSubmit : handleSubmit}
                    />
                </div>
            }
            content={
                <>
                    {/* Header Row */}
                    <div
                        style={{
                            display: "flex",
                            gap: 12,
                            marginBottom: 10,
                            fontWeight: 600,
                            color: "#374151",
                            fontSize: "14px",
                        }}
                    >
                        <div style={{ width: 50 }}>S.No</div>

                        <div style={{ flex: 1 }}>
                            Plant Code
                        </div>

                        <div style={{ flex: 1 }}>
                            Parent Vendor
                        </div>

                        <div style={{ flex: 1 }}>
                            Child Vendor
                        </div>

                        <div style={{ flex: 1 }}>
                            Business Category
                        </div>

                        <div style={{ width: 150 }}>
                            Review Period (Days)
                        </div>

                        {/* <div style={{ width: 50 }}></div> */}
                    </div>

                    {rows.map((row, index) => (
                        <div
                            key={row.rowKey}
                            style={{
                                display: "flex",
                                gap: 12,
                                marginBottom: 15,
                                alignItems: "center",
                            }}
                        >
                            <div style={{ width: 50 }}>
                                {index + 1}
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    id={`plant-${index}`}
                                    name="plant"
                                    options={getFilteredOptions(
                                        row,
                                        "plantCode",
                                        plantOptions,
                                        "plant"
                                    )}
                                    height={40}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    selectedItemsData={row.plantCode}
                                    onChangeItems={(n, items) =>
                                        updateField(
                                            index,
                                            "plantCode",
                                            items
                                        )
                                    }
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    id={`parent-${index}`}
                                    name="parent"
                                    height={40}
                                    options={getFilteredOptions(
                                        row,
                                        "parentVendorId",
                                        parentVendorOptions,
                                        "parentVendor"
                                    )}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    selectedItemsData={row.parentVendorId}
                                    onChangeItems={(n, items) =>
                                        updateField(index, "parentVendorId", items)
                                    }
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    id={`child-${index}`}
                                    name="child"
                                    height={40}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    options={getFilteredOptions(
                                        row,
                                        "childVendorId",
                                        childVendorOptions,
                                        "vendorCode"
                                    )}
                                    selectedItemsData={row.childVendorId}
                                    onChangeItems={(n, items) =>
                                        updateField(index, "childVendorId", items)
                                    }
                                    copyPasteDisabled={false}
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <SingleCheckboxDropdown
                                    id={`cat-${index}`}
                                    name="category"
                                    options={getFilteredOptions(
                                        row,
                                        "businessCategory",
                                        categoryOptions,
                                        "businessCategory"
                                    )}
                                    height={40}
                                    copyPasteDisabled={false}
                                    ListboxComponentProps={false}
                                    selectedItemsData={row.businessCategory}
                                    copyPasteDisabled={false}
                                    onChangeItems={(n, items) =>
                                        updateField(index, "businessCategory", items)
                                    }
                                />
                            </div>

                            <div style={{ width: 150 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    placeholder="Days"
                                    value={row.reviewPeriod}
                                    onChange={(e) =>
                                        updateField(
                                            index,
                                            "reviewPeriod",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            {/* <div style={{ width: 50 }}>
                                <IconButton
                                    onClick={() => handleDelete(index)}
                                    disabled={rows.length === 1}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </div> */}
                        </div>
                    ))}

                    {/* {!editData && <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 15,
                        }}
                    >
                        <ButtonComponent
                            type="primary"
                            label="+ Add Configuration"
                            onClick={handleAddRow}
                            disabled={!isAllRowsValid}
                        />
                    </div>} */}
                </>
            }
        />
    );
};

export default AddPlantConfigurationModal;